import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ClipboardCheck, FileText, Image as ImageIcon, Plus, Trash2, UserRound } from 'lucide-react';
import { AssinaturaManager } from './assinatura-manager';
import { SelecaoUsuario } from './selecao-usuario';
import { useProjeto } from '@/contexts/projeto-hook';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';
import { USUARIO_PADRAO_LAUAN, USUARIO_PADRAO_LUCIANO } from '@/constants/usuarios-padrao';
import {
  PRAXIS_DOCUMENT_INPUT_SECTIONS,
  PRAXIS_LIMITES_ADICIONAIS_PADRAO,
  PRAXIS_LIMITES_ESPACO_PADRAO,
  coletarPendenciasPraxis,
  type PraxisContato,
  type PraxisChecklistItem,
  type PraxisChecklistStatus,
  normalizarChecklistPraxis,
  normalizarPraxisCamposDocumento,
} from '@/types/praxis-document';

interface Etapa1DadosMaquinaPraxisProps {
  dados: any;
  onChange?: (campo: string, valor: any) => void;
  modoEdicao?: boolean;
}

function CampoTexto({
  label,
  value,
  onChange,
  modoEdicao,
  type = 'text',
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  modoEdicao?: boolean;
  type?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{label}</label>
      {modoEdicao ? (
        <input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
        />
      ) : (
        <p className="text-sm text-gray-900 dark:text-white">{value || '-'}</p>
      )}
    </div>
  );
}

function CampoTextarea({
  label,
  value,
  onChange,
  modoEdicao,
  rows = 3,
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  modoEdicao?: boolean;
  rows?: number;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{label}</label>
      {modoEdicao ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
        />
      ) : (
        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{value || '-'}</p>
      )}
    </div>
  );
}

function ListaContatos({
  titulo,
  contatos,
  modoEdicao,
  onChange,
}: {
  titulo: string;
  contatos: PraxisContato[];
  modoEdicao?: boolean;
  onChange: (contatos: PraxisContato[]) => void;
}) {
  const { t } = useI18n();
  const contatosExibidos = contatos.length > 0 ? contatos : [{ nome: '', funcao: '', contato: '' }];

  const atualizarContato = (index: number, campo: keyof PraxisContato, valor: string) => {
    onChange(contatosExibidos.map((contato, contatoIndex) => (
      contatoIndex === index ? { ...contato, [campo]: valor } : contato
    )));
  };

  const adicionarContato = () => {
    onChange([...contatos, { nome: '', funcao: '', contato: '' }]);
  };

  const removerContato = (index: number) => {
    onChange(contatos.filter((_, contatoIndex) => contatoIndex !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold text-gray-900 dark:text-white">{titulo}</h3>
        {modoEdicao && (
          <button
            type="button"
            onClick={adicionarContato}
            className="inline-flex items-center gap-2 rounded-md border border-schmersal-blue/40 px-3 py-1.5 text-sm text-schmersal-blue hover:bg-schmersal-blue/5 dark:text-schmersal-yellow dark:border-schmersal-yellow/40"
          >
            <Plus className="h-4 w-4" />
            {t('comum.acao.adicionar')}
          </button>
        )}
      </div>
      <div className="space-y-3">
        {contatosExibidos.map((contato, index) => (
          <div key={`${titulo}-${index}`} className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
            <CampoTexto label={index === 0 ? t('comum.rotulo.nome') : ''} value={contato.nome} modoEdicao={modoEdicao} onChange={(value) => atualizarContato(index, 'nome', value)} />
            <CampoTexto label={index === 0 ? t('apreciacaoEdicao.praxis.contatos.funcao') : ''} value={contato.funcao} modoEdicao={modoEdicao} onChange={(value) => atualizarContato(index, 'funcao', value)} />
            <CampoTexto label={index === 0 ? t('apreciacaoEdicao.praxis.contatos.dadosContato') : ''} value={contato.contato} modoEdicao={modoEdicao} onChange={(value) => atualizarContato(index, 'contato', value)} />
            {modoEdicao && contatos.length > 0 && (
              <button
                type="button"
                onClick={() => removerContato(index)}
                className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/60 dark:hover:bg-red-950/30"
                title={t('apreciacaoEdicao.praxis.contatos.removerTitulo')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListaLinhasTexto({
  label,
  value,
  onChange,
  modoEdicao,
}: {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  modoEdicao?: boolean;
}) {
  return (
    <CampoTextarea
      label={label}
      value={value.join('\n')}
      modoEdicao={modoEdicao}
      rows={5}
      onChange={(texto) => onChange(texto.split('\n').map((linha) => linha.trim()).filter(Boolean))}
    />
  );
}

export function Etapa1DadosMaquinaPraxis({
  dados,
  onChange,
  modoEdicao = false,
}: Etapa1DadosMaquinaPraxisProps) {
  const { t, formatarData } = useI18n();
  const { projeto } = useProjeto();
  const empresaId = dados?.projeto?.empresaId || projeto?.empresaId;
  const [executanteUsuario, setExecutanteUsuario] = useState<any>(null);
  const camposCliente = dados.camposCliente || {};
  const praxis = useMemo(
    () => normalizarPraxisCamposDocumento(camposCliente.praxis, projeto?.nome || 'SENAI'),
    [camposCliente.praxis, projeto?.nome]
  );
  const limites = praxis.limitesMaquina;
  const checklist = useMemo(() => normalizarChecklistPraxis(dados.checklistTecnicoAdmin), [dados.checklistTecnicoAdmin]);
  const pendencias = useMemo(() => coletarPendenciasPraxis({
    ...dados,
    camposCliente: {
      ...camposCliente,
      praxis,
    },
    checklistTecnicoAdmin: checklist,
  }), [dados, camposCliente, praxis, checklist]);

  useEffect(() => {
    const userId = dados.executanteUserId || dados.elaboradorUserId || dados.responsavelTecnicoUserId;
    if (userId && !dados.executanteExterno && !dados.elaboradorExterno && !dados.responsavelTecnicoExterno) {
      apiService.buscarUsuario(userId).then(response => {
        if (response.success && response.data) setExecutanteUsuario(response.data);
      });
    } else {
      setExecutanteUsuario(null);
    }
  }, [
    dados.executanteUserId,
    dados.elaboradorUserId,
    dados.responsavelTecnicoUserId,
    dados.executanteExterno,
    dados.elaboradorExterno,
    dados.responsavelTecnicoExterno,
  ]);

  const atualizarPraxis = (patch: Record<string, any>) => {
    onChange?.('camposCliente', {
      ...camposCliente,
      praxis: {
        ...praxis,
        ...patch,
      },
    });
  };

  const atualizarLimite = (index: number, campo: string, valor: string) => {
    const novosLimites = limites.map((item: any, itemIndex: number) => (
      itemIndex === index ? { ...item, [campo]: valor } : item
    ));
    atualizarPraxis({ limitesMaquina: novosLimites });
  };

  const atualizarChecklist = (grupoIndex: number, itemIndex: number, patch: Partial<PraxisChecklistItem>) => {
    const novosGrupos = checklist.grupos.map((grupo, gi) => {
      if (gi !== grupoIndex) return grupo;
      return {
        ...grupo,
        itens: grupo.itens.map((item, ii) => ii === itemIndex ? { ...item, ...patch } : item)
      };
    });
    onChange?.('checklistTecnicoAdmin', {
      ...checklist,
      grupos: novosGrupos
    });
  };

  const handleUsuarioChange = async (usuarioId: string | null, usuarioExterno: any) => {
    if (usuarioId) {
      onChange?.('executanteUserId', usuarioId);
      onChange?.('executanteExterno', null);
      onChange?.('elaboradorUserId', usuarioId);
      onChange?.('elaboradorExterno', null);
      onChange?.('responsavelTecnicoUserId', usuarioId);
      onChange?.('responsavelTecnicoExterno', null);
      const response = await apiService.buscarUsuario(usuarioId);
      if (response.success && response.data) setExecutanteUsuario(response.data);
      return;
    }

    if (usuarioExterno) {
      onChange?.('executanteExterno', usuarioExterno);
      onChange?.('executanteUserId', null);
      onChange?.('elaboradorExterno', usuarioExterno);
      onChange?.('elaboradorUserId', null);
      onChange?.('responsavelTecnicoExterno', usuarioExterno);
      onChange?.('responsavelTecnicoUserId', null);
      setExecutanteUsuario(null);
      return;
    }

    onChange?.('executanteUserId', null);
    onChange?.('executanteExterno', null);
    onChange?.('elaboradorUserId', null);
    onChange?.('elaboradorExterno', null);
    onChange?.('responsavelTecnicoUserId', null);
    onChange?.('responsavelTecnicoExterno', null);
    setExecutanteUsuario(null);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-schmersal-blue/30 dark:border-schmersal-blue">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <FileText className="h-6 w-6 text-schmersal-blue mr-2" />
            {t('apreciacaoEdicao.praxis.titulo')}
          </h2>
          <span className="rounded-full bg-schmersal-blue/10 px-3 py-1 text-xs font-semibold text-schmersal-blue dark:text-schmersal-yellow dark:bg-schmersal-yellow/10">
            tec.nicum + SCHMERSAL
          </span>
        </div>

        {pendencias.length > 0 && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/30">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                  {t('apreciacaoEdicao.praxis.pendencias.titulo')}
                </h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {pendencias.slice(0, 10).map((pendencia) => (
                    <span key={`${pendencia.secao}-${pendencia.chave}`} className="rounded-full bg-white px-2.5 py-1 text-xs text-amber-900 border border-amber-200 dark:bg-schmersal-gray-darkest dark:text-amber-200 dark:border-amber-800">
                      {pendencia.label}
                    </span>
                  ))}
                  {pendencias.length > 10 && (
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs text-amber-900 border border-amber-200 dark:bg-schmersal-gray-darkest dark:text-amber-200 dark:border-amber-800">
                      {t('apreciacaoEdicao.praxis.pendencias.restantes', { quantidade: pendencias.length - 10 })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6 rounded-lg border border-schmersal-blue/20 bg-schmersal-blue/5 p-4 dark:border-schmersal-blue/40 dark:bg-schmersal-blue/10">
          <h3 className="text-sm font-semibold text-schmersal-blue dark:text-schmersal-yellow mb-2">
            {t('apreciacaoEdicao.praxis.entradas.titulo')}
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {PRAXIS_DOCUMENT_INPUT_SECTIONS.map((secao) => (
              <div key={secao.secao} className="rounded-md bg-white/80 dark:bg-schmersal-gray-darkest/70 p-3 border border-gray-200 dark:border-schmersal-gray-dark">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{secao.secao}</p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{secao.descricao}</p>
                <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">
                  {secao.campos.map((campo) => `${campo.label}${campo.obrigatorio ? ' *' : ''}`).join(', ')}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <CampoTexto
            label={t('apreciacaoEdicao.praxis.documento.numero')}
            value={dados.numeroDocumento || praxis.numeroDocumentoPraxis || ''}
            modoEdicao={modoEdicao}
            onChange={(value) => {
              onChange?.('numeroDocumento', value);
              atualizarPraxis({ numeroDocumentoPraxis: value });
            }}
          />
          <CampoTexto
            label={t('apreciacaoEdicao.praxis.documento.clienteFinal')}
            value={praxis.clienteFinal || projeto?.nome || 'SENAI'}
            modoEdicao={modoEdicao}
            onChange={(value) => atualizarPraxis({ clienteFinal: value })}
          />
          <CampoTexto
            label={t('apreciacaoEdicao.praxis.documento.dataEmissao')}
            type="date"
            value={dados.dataEmissao ? String(dados.dataEmissao).substring(0, 10) : ''}
            modoEdicao={modoEdicao}
            onChange={(value) => onChange?.('dataEmissao', value)}
          />
          <div className="md:col-span-2 lg:col-span-3">
            <CampoTextarea
              label={t('apreciacaoEdicao.praxis.documento.descricaoServico')}
              value={praxis.descricaoServico || dados.titulo || ''}
              modoEdicao={modoEdicao}
              rows={3}
              onChange={(value) => atualizarPraxis({ descricaoServico: value })}
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.praxis.contatos.titulo')}</h2>
        <div className="space-y-6">
          <ListaContatos
            titulo={t('apreciacaoEdicao.praxis.contatos.cliente')}
            contatos={praxis.contatosCliente}
            modoEdicao={modoEdicao}
            onChange={(contatos) => atualizarPraxis({ contatosCliente: contatos })}
          />
          <ListaContatos
            titulo={t('apreciacaoEdicao.praxis.contatos.schmersal')}
            contatos={praxis.contatosSchmersal}
            modoEdicao={modoEdicao}
            onChange={(contatos) => atualizarPraxis({ contatosSchmersal: contatos })}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ListaLinhasTexto
              label={t('apreciacaoEdicao.praxis.normas.nacionais')}
              value={praxis.normasNacionais}
              modoEdicao={modoEdicao}
              onChange={(normasNacionais) => atualizarPraxis({ normasNacionais })}
            />
            <ListaLinhasTexto
              label={t('apreciacaoEdicao.praxis.normas.internacionais')}
              value={praxis.normasInternacionais}
              modoEdicao={modoEdicao}
              onChange={(normasInternacionais) => atualizarPraxis({ normasInternacionais })}
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.praxis.maquina.titulo')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <CampoTexto label={t('apreciacaoEdicao.praxis.maquina.nome')} value={dados.nomeMaquina || dados.maquina?.nome || ''} modoEdicao={modoEdicao} onChange={(value) => onChange?.('nomeMaquina', value)} />
          <CampoTexto label={t('comum.rotulo.tipo')} value={dados.tipoMaquina || ''} modoEdicao={modoEdicao} onChange={(value) => onChange?.('tipoMaquina', value)} />
          <CampoTexto label={t('apreciacaoEdicao.praxis.maquina.identificacao')} value={dados.identificacaoMaquina || ''} modoEdicao={modoEdicao} onChange={(value) => onChange?.('identificacaoMaquina', value)} />
          <CampoTexto label={t('apreciacaoEdicao.praxis.maquina.setor')} value={dados.setorMaquina || dados.maquina?.area || ''} modoEdicao={modoEdicao} onChange={(value) => onChange?.('setorMaquina', value)} />
          <CampoTexto label={t('apreciacaoEdicao.praxis.maquina.revisao')} value={String(dados.revisao ?? 1)} modoEdicao={modoEdicao} onChange={(value) => onChange?.('revisao', Number(value) || 0)} />
        </div>
        <div className="mt-4">
          <CampoTextarea
            label={t('apreciacaoEdicao.praxis.maquina.funcao')}
            value={dados.funcaoMaquina || dados.maquina?.descricao || ''}
            modoEdicao={modoEdicao}
            rows={3}
            onChange={(value) => onChange?.('funcaoMaquina', value)}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <ImageIcon className="h-6 w-6 text-schmersal-blue mr-2" />
          {t('apreciacaoEdicao.praxis.fotos.titulo')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CampoTexto label={t('apreciacaoEdicao.praxis.fotos.urlPanoramica')} value={dados.fotoPanoramicaUrl || ''} modoEdicao={modoEdicao} onChange={(value) => onChange?.('fotoPanoramicaUrl', value)} />
          <CampoTexto label={t('apreciacaoEdicao.praxis.fotos.urlIdentificacao')} value={dados.fotoIdentificacaoUrl || ''} modoEdicao={modoEdicao} onChange={(value) => onChange?.('fotoIdentificacaoUrl', value)} />
        </div>
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          {t('apreciacaoEdicao.praxis.fotos.aviso')}
        </p>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <UserRound className="h-6 w-6 text-schmersal-blue mr-2" />
          {t('apreciacaoEdicao.praxis.assinatura.titulo')}
        </h2>
        {modoEdicao ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 self-center mr-1">{t('apreciacaoEdicao.praxis.usuarioPadrao')}</span>
              <button type="button" onClick={() => handleUsuarioChange(null, { ...USUARIO_PADRAO_LAUAN })} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark">
                {t('apreciacaoEdicao.praxis.usarUsuario', { nome: 'Lauan' })}
              </button>
              <button type="button" onClick={() => handleUsuarioChange(null, { ...USUARIO_PADRAO_LUCIANO })} className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark">
                {t('apreciacaoEdicao.praxis.usarUsuario', { nome: 'Luciano' })}
              </button>
            </div>
            <SelecaoUsuario
              label={t('apreciacaoEdicao.praxis.assinatura.consultorTecnico')}
              rotuloNome={t('apreciacaoEdicao.praxis.assinatura.consultorTecnico')}
              empresaId={empresaId || undefined}
              usuarioId={dados.executanteUserId || dados.elaboradorUserId || dados.responsavelTecnicoUserId}
              usuarioExterno={dados.executanteExterno || dados.elaboradorExterno || dados.responsavelTecnicoExterno}
              onChange={handleUsuarioChange}
              modoEdicao={modoEdicao}
            />
            <AssinaturaManager
              assinaturaUrl={dados.executanteExterno?.assinaturaUrl || dados.elaboradorExterno?.assinaturaUrl || dados.responsavelTecnicoExterno?.assinaturaUrl || executanteUsuario?.assinaturaUrl}
              assinaturaDigital={dados.executanteExterno?.assinaturaDigital || dados.elaboradorExterno?.assinaturaDigital || dados.responsavelTecnicoExterno?.assinaturaDigital || executanteUsuario?.assinaturaDigital}
              onChange={(url, digital) => {
                const externo = dados.executanteExterno || dados.elaboradorExterno || dados.responsavelTecnicoExterno || {};
                const atualizado = { ...externo, assinaturaUrl: url, assinaturaDigital: digital };
                onChange?.('executanteExterno', atualizado);
                onChange?.('elaboradorExterno', atualizado);
                onChange?.('responsavelTecnicoExterno', atualizado);
              }}
              modoEdicao={modoEdicao}
            />
          </div>
        ) : (
          <p className="text-sm text-gray-900 dark:text-white">
            {dados.executanteExterno?.nome || dados.executante?.nome || dados.responsavelTecnicoExterno?.nome || dados.responsavelTecnico?.nome || '-'}
          </p>
        )}
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.praxis.limites.titulo')}</h2>
        <div className="space-y-3">
          {limites.map((item: any, index: number) => (
            <div key={`${item.codigo}-${index}`} className="grid grid-cols-1 lg:grid-cols-[90px_260px_1fr] gap-3 items-start">
              <div className="text-sm font-bold text-schmersal-blue dark:text-schmersal-yellow pt-2">
                {item.codigo}
              </div>
              <CampoTexto
                label={index === 0 ? t('apreciacaoEdicao.praxis.limites.tituloColuna') : ''}
                value={item.titulo || ''}
                modoEdicao={modoEdicao}
                onChange={(value) => atualizarLimite(index, 'titulo', value)}
              />
              <div>
                {index === 0 && <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('apreciacaoEdicao.praxis.limites.textoLivre')}</label>}
                {modoEdicao ? (
                  <textarea
                    value={item.texto || ''}
                    onChange={(e) => atualizarLimite(index, 'texto', e.target.value)}
                    rows={2}
                    className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
                    placeholder={`${item.codigo}----${item.titulo || 'ITEM'}:`}
                  />
                ) : (
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{item.texto || '-'}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CampoTextarea
            label={t('apreciacaoEdicao.praxis.limites.espaco')}
            value={dados.limitesEspaco || PRAXIS_LIMITES_ESPACO_PADRAO}
            modoEdicao={modoEdicao}
            rows={5}
            onChange={(value) => onChange?.('limitesEspaco', value)}
          />
          <CampoTextarea
            label={t('apreciacaoEdicao.praxis.limites.adicionais')}
            value={dados.limitesAdicionais || PRAXIS_LIMITES_ADICIONAIS_PADRAO}
            modoEdicao={modoEdicao}
            rows={5}
            onChange={(value) => onChange?.('limitesAdicionais', value)}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <ClipboardCheck className="h-6 w-6 text-schmersal-blue mr-2" />
          {t('apreciacaoEdicao.praxis.checklist.titulo')}
        </h2>
        <div className="space-y-6">
          {checklist.grupos.map((grupo, grupoIndex) => (
            <div key={grupo.titulo} className="overflow-x-auto">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{grupo.titulo}</h3>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
                <thead className="bg-gray-50 dark:bg-schmersal-gray-dark">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-white">{t('apreciacaoEdicao.praxis.checklist.colunaItem')}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-white">{t('comum.rotulo.descricao')}</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 dark:text-white">{t('comum.rotulo.status')}</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-white">{t('comum.rotulo.observacoes')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-schmersal-gray-dark">
                  {grupo.itens.map((item, itemIndex) => (
                    <tr key={item.codigo} className="hover:bg-gray-50 dark:hover:bg-schmersal-gray-dark/60">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white">{item.codigo}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">{item.descricao}</td>
                      <td className="px-3 py-2">
                        {modoEdicao ? (
                          <select
                            value={item.status}
                            onChange={(e) => atualizarChecklist(grupoIndex, itemIndex, { status: e.target.value as PraxisChecklistStatus })}
                            className="w-full rounded-md border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-gray-900 dark:text-white"
                          >
                            <option value="OK">OK</option>
                            <option value="NOK">NOK</option>
                            <option value="N/A">N/A</option>
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900 dark:text-white">{item.status}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {modoEdicao ? (
                          <input
                            type="text"
                            value={item.observacao || ''}
                            onChange={(e) => atualizarChecklist(grupoIndex, itemIndex, { observacao: e.target.value })}
                            className="w-full rounded-md border-gray-300 dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darkest text-sm text-gray-900 dark:text-white"
                            placeholder={t('apreciacaoEdicao.praxis.checklist.placeholderObservacao')}
                          />
                        ) : (
                          <span className="text-sm text-gray-700 dark:text-gray-300">{item.observacao || '-'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-schmersal-gray-darker shadow rounded-lg p-6 border border-rg-line dark:border-schmersal-gray-dark">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('apreciacaoEdicao.praxis.conclusao.titulo')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CampoTexto label={t('apreciacaoEdicao.praxis.conclusao.cidade')} value={dados.cidadeAssinatura || 'Boituva'} modoEdicao={modoEdicao} onChange={(value) => onChange?.('cidadeAssinatura', value)} />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('apreciacaoEdicao.praxis.conclusao.dataAprovacao')}</label>
            {modoEdicao ? (
              <input
                type="date"
                value={dados.dataAprovacao ? String(dados.dataAprovacao).substring(0, 10) : ''}
                onChange={(e) => onChange?.('dataAprovacao', e.target.value)}
                className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm"
              />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white">{dados.dataAprovacao ? formatarData(dados.dataAprovacao, { timeZone: 'UTC' }) : '-'}</p>
            )}
          </div>
        </div>
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('apreciacaoEdicao.praxis.conclusao.conclusoesFinais')}</label>
            {modoEdicao ? (
              <textarea value={dados.conclusoesFinais || ''} onChange={(e) => onChange?.('conclusoesFinais', e.target.value)} rows={4} className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm" />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{dados.conclusoesFinais || '-'}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('apreciacaoEdicao.praxis.conclusao.exclusaoResponsabilidade')}</label>
            {modoEdicao ? (
              <textarea value={dados.exclusaoResponsabilidade || ''} onChange={(e) => onChange?.('exclusaoResponsabilidade', e.target.value)} rows={4} className="w-full border-gray-300 dark:border-schmersal-gray-dark rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-schmersal-gray-darkest text-gray-900 dark:text-white sm:text-sm" />
            ) : (
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{dados.exclusaoResponsabilidade || '-'}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
